import { CSSProperties } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import useScreen from 'Hooks/useScreen'
import useMaestroChat, { getWorkspaceInfo } from 'Hooks/useMaestroChat'
import { styx } from 'modules/Utils'
import Colors from 'modules/Colors'
import AssistantWorkspaceWrapper from './AssistantWorkspaceWrapper'
import AssistantWorkspaceWebsitePreview from './AssistantWorkspaceWebsitePreview'
import AssistantWorkspaceTemplateSelection from './AssistantWorkspaceTemplateSelection'
import AssistantWorkspaceFacebookConnect from './AssistantWorkspaceFacebookConnect'
import AssistantWorkspaceMarketingPlan from './AssistantWorkspaceMarketingPlan'
import AssistantWorkspaceContentAnalysis from './AssistantWorkspaceContentAnalysis'
import AssistantWorkspaceInstagramPostSelection from './AssistantWorkspaceInstagramPostSelection'
import AssistantWorkspaceCampaignApproval from './AssistantWorkspaceCampaignApproval'

interface MaestroWorkspaceProps {
    /** Function to send messages to the chat */
    handleSendMessage: (message?: string, hiddenData?: { templateData?: any }) => void
}

const MaestroWorkspace = ({ 
    handleSendMessage
}: MaestroWorkspaceProps) => {
    const { mobileView, mStyle } = useScreen()
    
    const {
        activeWorkspace,
        setActiveWorkspace,
        isBuilding,
        buildingMessage,
    } = useMaestroChat()

    // Derived workspace state
    const isWorkspaceVisible = !!activeWorkspace
    const workspaceContent = activeWorkspace ? {
        type: activeWorkspace.type as 'report' | 'preview' | 'component' | 'custom' | 'website' | 'templateSelection' | 'campaignPreview' | 'facebookConnect' | 'marketingPlan' | 'instagramPostSelection' | 'contentAnalysis',
        data: activeWorkspace.data,
        title: undefined,
        isLoading: false
    } : null

    const hideWorkspace = () => {
        setActiveWorkspace(null)
    }

    // Render workspace content (shared between desktop and mobile)
    const renderWorkspaceContent = () => {
        if (!workspaceContent) return null

        const { type, data } = workspaceContent

        switch (type) {
            case 'website':
                return (
                    <AssistantWorkspaceWebsitePreview
                        workspaceContent={workspaceContent as any}
                        handleSendMessage={handleSendMessage}
                    />
                )
            case 'templateSelection':
                return (
                    <AssistantWorkspaceTemplateSelection
                        workspaceContent={workspaceContent as any}
                        handleSendMessage={handleSendMessage}
                    />
                )
            case 'campaignPreview':
                return (
                    <AssistantWorkspaceCampaignApproval
                        workspaceContent={workspaceContent as any}
                        handleSendMessage={handleSendMessage}
                    />
                )
            case 'facebookConnect':
                return (
                    <AssistantWorkspaceFacebookConnect
                        workspaceContent={workspaceContent as any}
                        handleSendMessage={handleSendMessage}
                        onClose={hideWorkspace}
                    />
                )
            case 'marketingPlan':
                return (
                    <AssistantWorkspaceMarketingPlan
                        workspaceContent={workspaceContent as any}
                        handleSendMessage={handleSendMessage}
                    />
                )
            case 'instagramPostSelection':
                return (
                    <AssistantWorkspaceInstagramPostSelection
                        workspaceContent={workspaceContent as any}
                        handleSendMessage={handleSendMessage}
                    />
                )
            case 'contentAnalysis':
                return (
                    <AssistantWorkspaceContentAnalysis
                        workspaceContent={workspaceContent as any}
                        handleSendMessage={handleSendMessage}
                    />
                )

            case 'report':
                return (
                    <div className="assistantWorkspaceReport" style={sty.assistantWorkspaceReport}>
                        <pre style={sty.assistantWorkspaceReportContent}>
                            {JSON.stringify(data, null, 2)}
                        </pre>
                    </div>
                )
            case 'preview':
                return (
                    <div className="assistantWorkspacePreview" style={sty.assistantWorkspacePreview}>
                        {data.imageUrl && (
                            <img
                                src={data.imageUrl}
                                alt="Preview"
                                style={sty.assistantWorkspacePreviewImage}
                            />
                        )}
                        {data.content && (
                            <div style={sty.assistantWorkspacePreviewContent}>
                                {data.content}
                            </div>
                        )}
                    </div>
                )
            case 'component':
                return (
                    <div className="assistantWorkspaceComponent" style={sty.assistantWorkspaceComponent}>
                        {data.component || <div>Custom component would render here</div>}
                    </div>
                )
            case 'custom':
                return (
                    <div className="assistantWorkspaceCustom" style={sty.assistantWorkspaceCustom}>
                        {data}
                    </div>
                )
            default:
                return <div>Unknown workspace type: {type}</div>
        }
    }

    // Render the workspace section (used for desktop side-by-side)
    const renderWorkspace = () => {
        if (!isWorkspaceVisible || !workspaceContent) {
            return null
        }

        const { type, title } = workspaceContent

        // Show building state if actively building
        if (isBuilding && buildingMessage) {
            return (
                <AssistantWorkspaceWrapper
                    title="Building Website"
                    icon="website"
                    isLoading={true}
                    onClose={hideWorkspace}
                />
            )
        }

        const workspaceInfo = getWorkspaceInfo(type)

        return (
            <AssistantWorkspaceWrapper
                title={title || workspaceInfo.title}
                icon={workspaceInfo.icon}
                onClose={hideWorkspace}
            >
                {renderWorkspaceContent()}
            </AssistantWorkspaceWrapper>
        )
    }

    // Render the workspace as a modal overlay (mobile only)
    const renderWorkspaceModal = () => {
        const { type, title } = workspaceContent || {}

        return (
            <motion.div
                key="workspace-modal"
                className="assistantWorkspaceModal"
                style={mStyle(sty, 'assistantWorkspaceModal')}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
            >
                {/* Backdrop */}
                <motion.div
                    className="assistantWorkspaceModalBackdrop"
                    style={sty.assistantWorkspaceModalBackdrop}
                    onClick={hideWorkspace}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                />

                {/* Modal Content */}
                <motion.div
                    className="assistantWorkspaceModalContent"
                    style={mStyle(sty, 'assistantWorkspaceModalContent')}
                    initial={{ opacity: 0, y: '100%' }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: '100%' }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                >
                    <AssistantWorkspaceWrapper
                        title={isBuilding ? "Building Website" : (title || (type && getWorkspaceInfo(type).title || ''))}
                        icon={isBuilding ? "website" : (type && getWorkspaceInfo(type).icon || '')}
                        isLoading={isBuilding}
                        onClose={hideWorkspace}
                        className="modalWorkspace"
                    >
                        {!isBuilding && renderWorkspaceContent()}
                    </AssistantWorkspaceWrapper>
                </motion.div>
            </motion.div>
        )
    }

    // Return the appropriate render based on mobileView
    if (mobileView) {
        return isWorkspaceVisible && workspaceContent ? renderWorkspaceModal() : null
    }

    return renderWorkspace()
}

const sty: Record<string, CSSProperties> = {
    /** Workspace styles */
    assistantWorkspaceReport: {
        width: '100%',
    },
    assistantWorkspaceReportContent: {
        backgroundColor: Colors.greyLight,
        padding: 16,
        borderRadius: 8,
        fontSize: 12,
        fontFamily: 'monospace',
        overflow: 'auto',
        maxHeight: 400,
        margin: 0,
    },
    assistantWorkspacePreview: {
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
    },
    assistantWorkspacePreviewImage: {
        maxWidth: '100%',
        height: 'auto',
        borderRadius: 8,
        border: `1px solid ${Colors.grey}`,
    },
    assistantWorkspacePreviewContent: {
        padding: 12,
        backgroundColor: Colors.greyLight,
        borderRadius: 8,
        fontSize: 14,
        lineHeight: 1.5,
    },
    assistantWorkspaceComponent: {
        width: '100%',
        minHeight: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: Colors.greyLight,
        borderRadius: 8,
        border: `2px dashed ${Colors.grey}`,
        color: Colors.greyDark,
        fontSize: 14,
    },
    assistantWorkspaceCustom: {
        width: '100%',
    },
    assistantWorkspaceModal: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000, // Above messages but below chat input
        padding: 16,
    },
    assistantWorkspaceModal_mobile: {
        padding: 0,
        alignItems: 'flex-end', // Slide up from bottom on mobile
    },
    assistantWorkspaceModalBackdrop: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        cursor: 'pointer',
    },
    assistantWorkspaceModalContent: {
        position: 'relative',
        backgroundColor: 'white',
        maxWidth: '90vw',
        maxHeight: '85vh',
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)',
        overflow: 'hidden',
        zIndex: 1001,
        marginBottom: 120, // Space for chat input
    },
    assistantWorkspaceModalContent_mobile: {
        maxWidth: '100vw',
        maxHeight: '100%', // Account for top bar + chat input
        marginBottom: 0
    },
    assistantWorkspaceModalContentInner: {
        flex: 1,
        overflowY: 'auto',
        padding: 24,
        paddingTop: 16,
    },
    assistantWorkspaceModalContentInner_mobile: {
        padding: 16,
        paddingTop: 12,
    },
}

export default MaestroWorkspace
